import { isPublicVariant } from "./expo";

import { AttributeValue } from "@/schemas/attribute/AttributeSet";
import {
  Seeking,
  UserSeekingAttributeSet,
} from "@/schemas/profile/attributes/UserSeekingAttributeSet";

export enum UserOnboardingStage {
  WELCOME_SCREEN = "WELCOME_SCREEN",
  PROFILE_LANGUAGE = "PROFILE_LANGUAGE",
  PROFILE_INTEREST = "PROFILE_INTEREST",
  PROFILE_PERSONAL_DETAILS = "PROFILE_PERSONAL_DETAILS",
  PROFILE_PHYSICAL_ATTRIBUTES = "PROFILE_PHYSICAL_ATTRIBUTES",
  PROFILE_AVATAR = "PROFILE_AVATAR",
  PUBLIC_ALBUM_PICS = "PUBLIC_ALBUM_PICS",
  DONE_ONBOARDING = "DONE_ONBOARDING",
}

export const PublicVariantOnboardingScreenStagesSequence: UserOnboardingStage[] =
  [
    UserOnboardingStage.WELCOME_SCREEN,
    UserOnboardingStage.PROFILE_LANGUAGE,
    UserOnboardingStage.PROFILE_INTEREST,
    UserOnboardingStage.PROFILE_PERSONAL_DETAILS,
    UserOnboardingStage.PROFILE_PHYSICAL_ATTRIBUTES,
    UserOnboardingStage.PROFILE_AVATAR,
    UserOnboardingStage.PUBLIC_ALBUM_PICS,
    UserOnboardingStage.DONE_ONBOARDING,
  ];

export const NonPublicVariantOnboardingScreenStagesSequence: UserOnboardingStage[] =
  [
    UserOnboardingStage.WELCOME_SCREEN,
    UserOnboardingStage.PROFILE_LANGUAGE,
    UserOnboardingStage.PROFILE_INTEREST,
    UserOnboardingStage.PROFILE_PERSONAL_DETAILS,
    UserOnboardingStage.PROFILE_PHYSICAL_ATTRIBUTES,
    UserOnboardingStage.PROFILE_AVATAR,
    UserOnboardingStage.PUBLIC_ALBUM_PICS,
    UserOnboardingStage.DONE_ONBOARDING,
  ];

///////////

type SeekingCardOption = {
  id: number;
  icon: string;
  title: string;
  description: string;
  seekingAttributes: Seeking[];
};

export const welcomeScreenSeekingCardOptions: SeekingCardOption[] = [
  {
    id: 0,
    icon: "🏳️‍🌈",
    title: "Community & Support",
    description: "Find community, connection, and belonging.",
    seekingAttributes: [
      Seeking.LGBTQ_COMMUNITY,
      Seeking.SUPPORT_GROUP,
      Seeking.EVENTS,
    ],
  },
  {
    id: 1,
    icon: "❤️",
    title: "Dating & Relationships",
    description: "Discover meaningful connections and romance.",
    seekingAttributes: [
      Seeking.RELATIONSHIP,
      Seeking.LONG_TERM,
      Seeking.ROMANCE,
      Seeking.DATES,
    ],
  },
  {
    id: 2,
    icon: "🤝",
    title: "Friends & Social",
    description: "Expand your social circle and make new friends.",
    seekingAttributes: [
      Seeking.FRIENDS,
      Seeking.CHAT,
      Seeking.COFFEE_DATES,
      Seeking.NETWORKING,
      Seeking.PLATONIC,
    ],
  },
  {
    id: 3,
    icon: "🎯",
    title: "Activities & Interests",
    description: "Connect through shared activities and hobbies.",
    seekingAttributes: [
      Seeking.ACTIVITY_PARTNERS,
      // Seeking.TRAVEL_BUDDY,
      // Seeking.GYM_BUDDY,
    ],
  },
  {
    id: 4,
    icon: "✨",
    title: "Casual Encounters",
    description: "Find spontaneous connections and casual meetups.",
    seekingAttributes: [Seeking.CASUAL, Seeking.RIGHT_NOW],
  },
];

export const getAllSeekingCardsAttributes = (): AttributeValue[] => {
  const allSeekingAttributes = welcomeScreenSeekingCardOptions.flatMap(
    (card) => card.seekingAttributes,
  );

  const uniqueSeekingAttributes = Array.from(new Set(allSeekingAttributes));

  return uniqueSeekingAttributes
    .map((id) => UserSeekingAttributeSet.map[id])
    .filter((v): v is AttributeValue => v !== undefined);
};

////////////

export interface ImageUploadPrompt {
  emoji: string;
  title: string;
  subtitle: string;
}

export const publicImagesUploadPrompts: ImageUploadPrompt[] = [
  { emoji: "🚶", title: "Full body shot", subtitle: "Show your style" },
  { emoji: "📷", title: "Selfie", subtitle: "Clear face photo" },
  { emoji: "👥", title: "With friends", subtitle: "Social moments" },
  { emoji: "💼", title: "Professional", subtitle: "Work appropriate" },
  { emoji: "🎯", title: "Hobby/Interest", subtitle: "What you love" },
  { emoji: "✨", title: "Your vibe", subtitle: "Express yourself" },
];

export const publicImagesUploadPromptsCount = publicImagesUploadPrompts.length;

////////////

export function isOnboardingCompleted(
  onboardingStage: UserOnboardingStage,
): boolean {
  return onboardingStage === UserOnboardingStage.DONE_ONBOARDING;
}

export function getNextOnboardingStage(
  currentStage: UserOnboardingStage,
): UserOnboardingStage {
  const OnboardingScreenStagesSequence = isPublicVariant()
    ? PublicVariantOnboardingScreenStagesSequence
    : NonPublicVariantOnboardingScreenStagesSequence;
  const currentIndex = OnboardingScreenStagesSequence.indexOf(currentStage);

  if (currentIndex === -1) return UserOnboardingStage.DONE_ONBOARDING;

  return (
    OnboardingScreenStagesSequence[currentIndex + 1] ??
    UserOnboardingStage.DONE_ONBOARDING
  );
}
